/**
 * Permission-gate component + hook. Mirrors Flutter `validatePermission(...)`
 * in scrips_core/utils/utils.dart — every key in `keys` must be present in
 * the user's `systemPermissions`, and any provided CRUD flags (add/delete/
 * edit/view) must match exactly. Returns `false` when the user has no
 * permissions at all (matches Flutter's empty-array short-circuit).
 *
 * The component renders nothing when the gate fails (no fallback prop on
 * purpose — UI hiding is the default healthcare convention).
 */

import type { ReactNode } from 'react';
import { useAuthStore } from './auth-store';
import type { PermissionResults, SystemPermission } from './auth-api';

export interface PermissionCheck {
  add?: boolean;
  delete?: boolean;
  edit?: boolean;
  view?: boolean;
}

function evaluate(
  permissions: SystemPermission[] | undefined,
  keys: string[],
  check: PermissionCheck,
): boolean {
  if (!permissions || permissions.length === 0) return false;

  return keys.every((key) =>
    permissions.some((p) => p.permissionKey === key && matchesCheck(p.permissionResults, check)),
  );
}

function matchesCheck(results: PermissionResults | undefined, check: PermissionCheck): boolean {
  if (check.add !== undefined && (results?.add ?? false) !== check.add) return false;
  if (check.delete !== undefined && (results?.delete ?? false) !== check.delete) return false;
  if (check.edit !== undefined && (results?.edit ?? false) !== check.edit) return false;
  if (check.view !== undefined && (results?.view ?? false) !== check.view) return false;
  return true;
}

/**
 * Hook form. Reads `systemPermissions` from the auth store and returns
 * `true` when every key passes the check.
 *
 *   const canEdit = usePermission(['Manage_Patients_Demographics'], { edit: true });
 */
export function usePermission(keys: string[], check: PermissionCheck = {}): boolean {
  const permissions = useAuthStore((s) => s.user?.systemPermissions);
  return evaluate(permissions, keys, check);
}

export interface PermissionGateProps extends PermissionCheck {
  keys: string[];
  children: ReactNode;
  /** Rendered when the user lacks the permission. Defaults to `null`. */
  fallback?: ReactNode;
}

/**
 *   <PermissionGate keys={['Patient_Contacts']} view>
 *     <ContactDetailsTab />
 *   </PermissionGate>
 *
 *   <PermissionGate keys={['Manage_Patients_Demographics']} edit fallback={<DisabledHint />}>
 *     <SaveButton />
 *   </PermissionGate>
 */
export function PermissionGate({
  keys,
  children,
  fallback = null,
  ...check
}: PermissionGateProps) {
  const allowed = usePermission(keys, check);
  return <>{allowed ? children : fallback}</>;
}
